import React, { useState } from 'react';
import styled from 'styled-components';
import ReactPlayer from 'react-player';
import { motion } from 'framer-motion';
import { PauseOutlined, PlayCircleOutlined, SoundOutlined,} from '@ant-design/icons';
import { theme } from '../../styles/theme';
import { ScrollReveal } from '../animations/ScrollReveal';

const VideoContainer = styled.div`
  padding: 100px 0;
  background-color: ${theme.colors.background};
`;

const VideoWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const VideoTitle = styled(motion.h2)`
  text-align: center;
  font-size: 36px;
  margin-bottom: 50px;
  color: ${theme.colors.text};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 28px;
  }
`;

const PlayerWrapper = styled.div`
  position: relative;
  padding-top: 56.25%; /* 16:9 宽高比 */
  background-color: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${theme.shadows.large};
`;

const StyledReactPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  left: 0;
`;

const VideoDescription = styled(motion.div)`
  max-width: 800px;
  margin: 40px auto 0;
  text-align: center;
  color: ${theme.colors.text};
  font-size: 18px;
  line-height: 1.6;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 16px;
    padding: 0 20px;
  }
`;

const VideoFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled(motion.div)`
  text-align: center;
  padding: 30px;
  background-color: ${theme.colors.white};
  border-radius: 8px;
  box-shadow: ${theme.shadows.small};
  transition: all ${theme.transitions.default};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.medium};
  }
`;

const FeatureTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 15px;
  color: ${theme.colors.primary};
`;

const FeatureDescription = styled.p`
  font-size: 16px;
  color: ${theme.colors.text};
  line-height: 1.5;
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0));
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
  height: auto;

  ${PlayerWrapper}:hover & {
    opacity: 1;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

const ProgressContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressBar = styled.input`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  appearance: none;
  outline: none;
  transition: all 0.3s ease;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    background: ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    opacity: 0;
  }

  &:hover {
    height: 6px;
    &::-webkit-slider-thumb {
      opacity: 1;
    }
  }
`;

const TimeDisplay = styled.div`
  color: white;
  font-size: 14px;
  min-width: 100px;
  text-align: right;
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 28px;
  display: flex;
  align-items: center;
  padding: 8px;
  transition: all 0.3s ease;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  justify-content: center;

  &:hover {
    color: ${theme.colors.primary};
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 150px;
  position: relative;

  &:hover {
    .volume-slider {
      width: 100px;
      opacity: 1;
    }
  }
`;

const VolumeSlider = styled.input`
  width: 0;
  opacity: 0;
  transition: all 0.3s ease;
  cursor: pointer;
  appearance: none;
  background: rgba(255, 255, 255, 0.2);
  height: 4px;
  border-radius: 2px;
  outline: none;
  margin: 0;
  padding: 0;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    background: white;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.2);
      background: ${theme.colors.primary};
    }
  }

  &.volume-slider {
    margin-left: 8px;
  }
`;

const SpeedControl = styled.select`
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 14px;
  border-radius: 4px;
  outline: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  option {
    background: #1a1a1a;
    padding: 8px;
  }
`;

const VolumeButton = styled(ControlButton)`
  &:hover + .volume-slider {
    width: 100px;
    opacity: 1;
  }
`;

const VolumeIcon = styled(SoundOutlined)`
  transition: all 0.3s ease;
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const MutedIcon = styled(({ ...props }) => <SoundOutlined {...props} />)`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: 24px;
    background-color: currentColor;
    transform: rotate(45deg);
    left: 50%;
    top: 0;
  }
`;

export const VideoSection: React.FC = () => {
  const [playing, setPlaying] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [prevVolume, setPrevVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);

  const formatTime = (seconds: number) => {
    const pad = (num: number) => num.toString().padStart(2, '0');
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${pad(minutes)}:${pad(remainingSeconds)}`;
  };

  const handleProgress = (state: { played: number; playedSeconds: number }) => {
    if (!seeking) {
      setProgress(state.played);
    }
  };

  const handleDuration = (duration: number) => {
    setDuration(duration);
  };

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProgress(parseFloat(e.target.value));
  };

  const handleSeekMouseDown = () => {
    setSeeking(true);
  };

  const handleSeekMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
    setSeeking(false);
    const player = playerRef.current;
    if (player) {
      player.seekTo(parseFloat((e.target as HTMLInputElement).value));
    }
  };

  const playerRef = React.useRef<ReactPlayer>(null);

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setPrevVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    if (isMuted) {
      setVolume(prevVolume);
      setIsMuted(false);
    } else {
      setPrevVolume(volume);
      setVolume(0);
      setIsMuted(true);
    }
  };

  const handlePlaybackRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPlaybackRate(parseFloat(e.target.value));
  };

  return (
    <VideoContainer>
      <VideoWrapper>
        <ScrollReveal>
          <VideoTitle>宣讲视频展示</VideoTitle>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <PlayerWrapper>
            <StyledReactPlayer
              ref={playerRef}
              url="./videos/presentation.mp4"
              width="100%"
              height="100%"
              playing={playing}
              volume={volume}
              playbackRate={playbackRate}
              muted={isMuted}
              loop
              playsinline
              controls={false}
              onProgress={handleProgress}
              onDuration={handleDuration}
              config={{
                file: {
                  attributes: {
                    controlsList: "nodownload",
                    autoPlay: true,
                    muted: true,
                  },
                },
              }}
            />
            <VideoControls>
              <ProgressContainer>
                <ProgressBar
                  type="range"
                  min={0}
                  max={1}
                  step={0.001}
                  value={progress}
                  onChange={handleSeekChange}
                  onMouseDown={handleSeekMouseDown}
                  onMouseUp={handleSeekMouseUp}
                />
                <TimeDisplay>
                  {formatTime(progress * duration)} / {formatTime(duration)}
                </TimeDisplay>
              </ProgressContainer>
              <ControlsRow>
                <ControlButton onClick={handlePlayPause}>
                  {playing ? <PauseOutlined /> : <PlayCircleOutlined />}
                </ControlButton>
                <VolumeControl>
                  <VolumeButton as="div" onClick={handleMuteToggle}>
                    {isMuted ? <MutedIcon /> : <VolumeIcon />}
                  </VolumeButton>
                  <VolumeSlider
                    className="volume-slider"
                    type="range"
                    min={0}
                    max={1}
                    step={0.1}
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                  />
                </VolumeControl>
                <SpeedControl
                  value={playbackRate}
                  onChange={handlePlaybackRateChange}
                >
                  <option value={0.5}>0.5x</option>
                  <option value={1}>1x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                </SpeedControl>
              </ControlsRow>
            </VideoControls>
          </PlayerWrapper>
        </ScrollReveal>

        <ScrollReveal delay={0.4}>
          <VideoDescription>
            全方位展示我们的智能助贷系统，包含完整的业务流程、风控管理、数据分析等核心功能
          </VideoDescription>
        </ScrollReveal>

        <VideoFeatures>
          <ScrollReveal delay={0.6}>
            <Feature>
              <FeatureTitle>聚合聊天</FeatureTitle>
              <FeatureDescription>
                跨账号统一管理，无缝沟通聊天
              </FeatureDescription>
            </Feature>
          </ScrollReveal>

          <ScrollReveal delay={0.7}>
            <Feature>
              <FeatureTitle>实时监控</FeatureTitle>
              <FeatureDescription>
                业务全流程实时监控，风险预警及时响应
              </FeatureDescription>
            </Feature>
          </ScrollReveal>

          <ScrollReveal delay={0.8}>
            <Feature>
              <FeatureTitle>数据分析</FeatureTitle>
              <FeatureDescription>
                多维度数据分析，为决策提供有力支持
              </FeatureDescription>
            </Feature>
          </ScrollReveal>
        </VideoFeatures>
      </VideoWrapper>
    </VideoContainer>
  );
};